import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, CheckCircle2, AlertTriangle } from 'lucide-react';
import { brandDependencyData } from './dependencyData';

export const StrategicRecommendation = () => {
  const { concentration, players } = brandDependencyData;
  const topPlayer = players[0];
  
  // Generate recommendations based on data
  const recommendations = [
    {
      priority: 'high',
      title: 'Diversification Required',
      text: `Current brand concentration exceeds optimal threshold (${concentration.top1Percent}% vs ${concentration.optimalThreshold}% target). Accelerate visibility programs for emerging players ahead of 2026 cycle.`,
    },
    {
      priority: 'medium',
      title: 'Next-Generation Development',
      text: `Identify 2-3 roster players with high commercial potential for expanded media exposure. Target 15% reduction in top-1 dependency by Q4 2025.`,
    },
    {
      priority: 'low',
      title: 'Contingency Planning',
      text: `Develop communications playbook for key player unavailability scenarios. Ensure sponsor relationship management protocols are in place.`,
    },
  ];

  return (
    <section className="mb-10" data-testid="strategic-recommendation-section">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="w-6 h-6 text-primary" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Strategic Recommendations</h2>
      </div>
      
      {/* Main Recommendation */}
      <Card className="bg-primary/5 border-primary/20 mb-6">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-primary/10">
              <AlertTriangle className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-2">Board-Level Assessment</h3>
              <p className="text-base text-foreground leading-relaxed" data-testid="main-recommendation">
                Current brand concentration exceeds optimal threshold ({concentration.top1Percent}% concentrated in {topPlayer.name}). 
                Diversification through emerging player visibility is recommended ahead of 2026 cycle. 
                Without intervention, USMNT remains exposed to significant single-point-of-failure risk in brand valuation.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Action Items */}
      <div className="grid md:grid-cols-3 gap-5">
        {recommendations.map((rec, i) => (
          <Card key={i} className="bg-card border-border/50">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className={`w-4 h-4 ${
                  rec.priority === 'high' ? 'text-destructive' : 
                  rec.priority === 'medium' ? 'text-warning' : 'text-stable'
                }`} />
                <span className={`text-xs font-semibold uppercase tracking-wider ${
                  rec.priority === 'high' ? 'text-destructive' : 
                  rec.priority === 'medium' ? 'text-warning' : 'text-stable'
                }`}>
                  {rec.priority} Priority
                </span>
              </div>
              <h4 className="font-semibold text-foreground mb-2">{rec.title}</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">{rec.text}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default StrategicRecommendation;
